/**
 * useKnowledgeBaseChat - Shared hook for Knowledge Base streaming chat
 * Used by both desktop Interface and mobile MobileLayout
 */

import { useCallback } from 'react';
import { chatService, ChatSource } from '@/services/kbService';

import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
export interface KnowledgeBaseSource {
  title?: string;
  url?: string;
  file_name?: string;
  file_type?: string;
  relevance_score?: number;
  snippet?: string;
  qdrant_id?: string;
}

export interface KnowledgeBaseChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  isComplete?: boolean;
  wasStreamed?: boolean;
  sources?: KnowledgeBaseSource[];
}

interface UseKnowledgeBaseChatOptions {
  onMessageUpdate: (messageId: string, updates: Partial<KnowledgeBaseChatMessage>) => void;
  onStreamComplete: () => void;
  onError?: (error: Error) => void;
  onSessionId?: (sessionId: string, sessionTitle?: string) => void;
  searchStrategy?: string;
  model?: string;
  sessionId?: string | null;
  /**
   * Optional Orgain Document id. When set, tabular SQL queries are
   * pinned to this spreadsheet (skipping the multi-candidate picker
   * on the backend). Use this when the user has selected a specific
   * uploaded file to ask questions about. Omitted = backend chooses.
   */
  documentId?: string | null;
}

/**
 * Hook for handling Knowledge Base streaming chat
 */
export function useKnowledgeBaseChat(options: UseKnowledgeBaseChatOptions) {
  const {
    onMessageUpdate,
    onStreamComplete,
    onError,
    onSessionId,
    searchStrategy = 'weighted_fusion',
    model = 'gpt-4o-mini',
    sessionId,
    documentId,
  } = options;

  /**
   * Convert KB API sources to our format
   */
  const formatSources = useCallback((sources: ChatSource[]): KnowledgeBaseSource[] => {
    return sources.map((s) => ({
      title: s.title,
      file_name: s.file_name,
      relevance_score: s.score,
      snippet: s.excerpt,
      qdrant_id: s.document_id,
    }));
  }, []);

  /**
   * Stream a message to the Knowledge Base
   */
  const streamMessage = useCallback(async (
    query: string,
    messageId: string
  ): Promise<void> => {
    let accumulated = '';
    let receivedSources: ChatSource[] = [];
    let finalContent = '';

    try {
      await chatService.streamChatMessage(
        query,
        {
          onChunk: (text: string) => {
            accumulated += text;
            onMessageUpdate(messageId, {
              content: accumulated,
              isComplete: false,
            });
          },
          onSources: (sources: ChatSource[]) => {
            receivedSources = sources;
            onMessageUpdate(messageId, {
              sources: formatSources(sources),
            });
          },
          onDone: () => {
            const contentToShow = finalContent || accumulated;
            const formattedSources = formatSources(receivedSources);
            onMessageUpdate(messageId, {
              content: contentToShow,
              sources: formattedSources.length > 0 ? formattedSources : undefined,
              isComplete: true,
              wasStreamed: true,
            });
            onStreamComplete();
          },
          onError: (error: Error) => {
            logger.error('stream error:', error);
            
            
            const errorCode = (error as { errorCode?: string })?.errorCode;
            const isBackendMessage =
              errorCode === 'LLM_KEYS_MISSING' ||
              errorCode === 'LLM_ERROR' ||
              errorCode === 'CHAT_FAILED';
            onMessageUpdate(messageId, {
              content: isBackendMessage
                ? error.message
                : getFriendlyErrorMessage(
                    error,
                    'Sorry, I ran into a problem answering that. Please try again.',
                  ),
              isComplete: true,
            });
            onStreamComplete();
            onError?.(error);
          },
          onContentUpdate: (text: string) => {
            finalContent = text;
          },
          onSessionId: (newSessionId: string, sessionTitle?: string) => {
            onSessionId?.(newSessionId, sessionTitle);
          },
        },
        searchStrategy,
        model,
        sessionId,
        documentId
      );
    } catch (error) {
      logger.error('Knowledge Base error:', error);

      // Fallback to non-streaming on error
      try {
        const response = await chatService.sendChatMessage(
          query,
          searchStrategy,
          model,
          documentId
        );

        const formattedSources = formatSources(response.sources || []);

        onMessageUpdate(messageId, {
          content: response.message,
          sources: formattedSources,
          isComplete: true,
          wasStreamed: true,
        });

        onStreamComplete();
      } catch (fallbackError) {
        logger.error('Fallback also failed:', fallbackError);

        onMessageUpdate(messageId, {
          content: getFriendlyErrorMessage(
            fallbackError,
            'Sorry, I ran into a problem answering that. Please try again.',
          ),
          isComplete: true,
        });

        onStreamComplete();
        onError?.(fallbackError as Error);
      }
    }
  }, [searchStrategy, model, sessionId, documentId, onMessageUpdate, onStreamComplete, onError, onSessionId, formatSources]);

  return {
    streamMessage,
    formatSources,
  };
}

export default useKnowledgeBaseChat;
